/**
 * Raid Room Clear Transition
 *
 * Displays a victory overlay with "ROOM CLEARED!" and RPG-style dialogs
 * from 1-2 random Pokemon from the player's team.
 */

import React, { useState, useEffect, useMemo } from "react";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { S3_BASE_URL } from "@/utils/s3";
import { ClientPokemon } from "@/lib/battle/entities/client-pokemon";
import classNames from "classnames";

// ============================================================================
// Types
// ============================================================================

interface RaidRoomClearTransitionProps {
  myPokemon: ClientPokemon[];
}

interface DialogEntry {
  pokemonName: string;
  basePokemonName: string;
  line: string;
  position: "left" | "right";
}

// ============================================================================
// Victory Lines (randomized per Pokemon)
// ============================================================================

const VICTORY_LINES_PRIMARY = [
  "Area secured. Let's keep moving!",
  "That's done. What's next?",
  "No time to rest, we press on!",
  "Clear! Everyone still standing?",
  "Victory! But the real fight is ahead.",
  "Well fought. The path is open.",
];

const VICTORY_LINES_SECONDARY = [
  "Right behind you!",
  "Let's go!",
  "Ready when you are.",
  "On it!",
  "Agreed!",
  "I'm with you!",
];

// ============================================================================
// Utility
// ============================================================================

const pickRandom = <T,>(arr: T[]): T =>
  arr[Math.floor(Math.random() * arr.length)]!;

const capitalizeWords = (str: string) =>
  str
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

// ============================================================================
// Component
// ============================================================================

export const RaidRoomClearTransition: React.FC<
  RaidRoomClearTransitionProps
> = ({ myPokemon }) => {
  const [phase, setPhase] = useState<"title" | "dialog" | "done">("title");
  const [dialogIndex, setDialogIndex] = useState(0);
  const [showDialog, setShowDialog] = useState(false);

  // Select 1 or 2 random Pokemon for dialogs
  const dialogs: DialogEntry[] = useMemo(() => {
    const alive = myPokemon.filter((p) => !p.IsDestroyed);
    if (alive.length === 0) return [];

    const shuffled = [...alive].sort(() => Math.random() - 0.5);
    const speakers = shuffled.slice(0, Math.min(2, shuffled.length));

    return speakers.map((p, idx) => ({
      pokemonName: capitalizeWords(p.Name),
      basePokemonName: p.base_pokemon_name,
      line:
        idx === 0
          ? pickRandom(VICTORY_LINES_PRIMARY)
          : pickRandom(VICTORY_LINES_SECONDARY),
      position: idx === 0 ? "left" : "right",
    }));
  }, [myPokemon]);

  // Phase transitions
  useEffect(() => {
    const timers: number[] = [];

    // Title appears, then dialog after 1s
    timers.push(
      window.setTimeout(() => {
        setPhase("dialog");
        setShowDialog(true);
      }, 1200),
    );

    return () => timers.forEach((t) => clearTimeout(t));
  }, []);

  // Auto-advance dialog
  useEffect(() => {
    if (phase !== "dialog" || !showDialog) return;

    const timer = window.setTimeout(() => {
      if (dialogIndex < dialogs.length - 1) {
        setDialogIndex((i) => i + 1);
      } else {
        setPhase("done");
      }
    }, 2500);

    return () => clearTimeout(timer);
  }, [phase, showDialog, dialogIndex, dialogs.length]);

  // Allow click to skip
  const handleClick = () => {
    if (phase === "title") {
      setPhase("dialog");
      setShowDialog(true);
    } else if (phase === "dialog") {
      if (dialogIndex < dialogs.length - 1) {
        setDialogIndex((i) => i + 1);
      } else {
        setPhase("done");
      }
    }
  };

  const currentDialog = dialogs[dialogIndex];

  return (
    <div
      className="absolute inset-0 z-[100] flex flex-col items-center justify-center bg-black/70 cursor-pointer"
      onClick={handleClick}
    >
      {/* ROOM CLEARED Title */}
      <div
        className={classNames(
          "transition-all duration-500 ease-out",
          phase === "title"
            ? "opacity-100 scale-100"
            : "opacity-80 scale-90 -translate-y-8",
        )}
      >
        <StrokedText
          fontSize={48}
          strokeWidth={8}
          strokeColor="#1a1a1a"
          fillColor="#4ade80"
        >
          ROOM CLEARED!
        </StrokedText>
      </div>

      {/* Dialog Box */}
      {phase !== "title" && currentDialog && (
        <div
          className={classNames(
            "absolute bottom-8 left-4 right-4 max-w-xl mx-auto transition-all duration-300",
            showDialog
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8",
          )}
        >
          <StandardFrame variant="night" rounded>
            <div className="flex items-start gap-4 p-3">
              {/* Portrait */}
              <div
                className={classNames(
                  "flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 border-pk-gray-4 bg-pk-gray-2",
                  currentDialog.position === "right" && "order-2",
                )}
              >
                <img
                  src={`${S3_BASE_URL}/pokemons/${currentDialog.basePokemonName}/portraits/Normal.png`}
                  alt={currentDialog.pokemonName}
                  className="w-full h-full object-contain"
                  style={{ imageRendering: "pixelated" }}
                />
              </div>

              {/* Text */}
              <div
                className={classNames(
                  "flex-1 flex flex-col gap-1",
                  currentDialog.position === "right" && "items-end text-right",
                )}
              >
                <span className="text-pk-yellow-3 font-bold text-sm font-vck">
                  {currentDialog.pokemonName}
                </span>
                <p className="text-pk-gray-5 text-base leading-snug">
                  "{currentDialog.line}"
                </p>
              </div>
            </div>
          </StandardFrame>

          {/* Tap hint */}
          <div className="text-center mt-2 text-pk-gray-4 text-xs animate-pulse">
            Tap to continue
          </div>
        </div>
      )}
    </div>
  );
};
